import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { map, Observable } from 'rxjs';
import { RoutePaths } from 'src/app/app.routes';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';

/**
 * Keeps the patient landing view in sync with the `patient-profile-revamp` flag on every
 * navigation — including deep links, bookmarks and refreshes, which the patient-search entry point
 * alone can't cover:
 *   - flag ON  → the classic order-history route redirects to the new patient-profile
 *   - flag OFF → the new patient-profile route redirects back to the classic order-history
 *
 * The page that matches the current flag state is allowed straight through, so there is no redirect
 * loop. Both routes carry the same `:id`, so the redirect just swaps the path and reuses the id.
 * Fail-safe: an unloaded or failed flag reads OFF, so users land on the proven order-history page.
 */
const redirectForFlag = (targetIsRevamp: boolean): CanActivateFn => {
  return (route): Observable<boolean | UrlTree> => {
    const featureFlags = inject(FeatureFlagService);
    const router = inject(Router);
    const id = route.paramMap.get('id') ?? '';

    return featureFlags.ensureLoaded().pipe(
      map((): boolean | UrlTree => {
        const revamp = featureFlags.revampEnabled();
        if (targetIsRevamp === revamp) {
          return true;
        }
        const destination = revamp
          ? RoutePaths.PatientProfile.Path
          : RoutePaths.OrderHistory.Path;
        return router.createUrlTree([destination.replace(':id', id)]);
      }),
    );
  };
};

/** order-history route: redirect to the new patient-profile when the revamp flag is ON. */
export const orderHistoryFlagGuard: CanActivateFn = redirectForFlag(false);

/** patient-profile route: redirect back to the classic order-history when the revamp flag is OFF. */
export const patientProfileFlagGuard: CanActivateFn = redirectForFlag(true);
